import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('mboa_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      console.error('Auth check failed:', err?.message);
      localStorage.removeItem('mboa_token');
      localStorage.removeItem('mboa_user');
      localStorage.removeItem('mboa_refresh');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const storeSession = (data) => {
    localStorage.setItem('mboa_token', data.token);
    if (data.refresh_token) localStorage.setItem('mboa_refresh', data.refresh_token);
    localStorage.setItem('mboa_user', JSON.stringify(data.user));
    setUser(data.user);
  };

  // Second step of the login when the account has two-factor authentication
  const loginWith2fa = async (challengeToken, code) => {
    const res = await api.post('/auth/2fa/login', { challenge_token: challengeToken, code });
    storeSession(res.data);
    return res.data;
  };

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    if (res.data.requires_2fa) return res.data; // caller asks for the 6-digit code
    localStorage.setItem('mboa_token', res.data.token);
    if (res.data.refresh_token) localStorage.setItem('mboa_refresh', res.data.refresh_token);
    localStorage.setItem('mboa_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    localStorage.setItem('mboa_token', res.data.token);
    if (res.data.refresh_token) localStorage.setItem('mboa_refresh', res.data.refresh_token);
    localStorage.setItem('mboa_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (err) { console.error('Logout error:', err?.message); }
    localStorage.removeItem('mboa_token');
    localStorage.removeItem('mboa_refresh');
    localStorage.removeItem('mboa_user');
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWith2fa, storeSession, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
